<template>
  <!-- Loading spinner overlay -->
  <div v-if="authLoading || eventsLoading || usersLoading"
    class="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
    <LoadingSpinner
      class="rounded-lg bg-white p-3.5 border-solid border-neutral-300 border shadow-lg pointer-events-auto" />
  </div>
  <div>
    <!-- Main content container -->
    <div class="flex items-start justify-center" :class="{
      'pointer-events-none animate-pulse':
        authLoading || eventsLoading || usersLoading,
    }">
      <div class="flex gap-1.5 flex-col lg:flex-row justify-center">
        <!-- First column: User Information and Balance -->
        <div class="flex flex-col gap-1.5 w-80 shrink-0">
          <PanelWindow title="Brugerinformation">
            <UserInformation :user="user" />
          </PanelWindow>
          <PanelWindow title="Fridag optælling">
            <!-- Add date range button -->
            <div class="flex gap-2">
              <button @click="openDateRangeModal"
                class="h-[40px] flex-1 flex items-center px-4 py-2 rounded-lg text-neutral-800 bg-neutral-100 hover:bg-neutral-200 justify-center">
                <span class="text-sm font-medium text-black">
                  {{ formatDateRange(startDate, endDate) }}
                </span>
              </button>
              <button @click="resetToCurrentYear" title="Nulstil datoer"
                class="h-[40px] w-[40px] px-2 py-2 rounded-lg text-neutral-800 bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center">
                <RotateCcw class="w-4 h-4" />
              </button>
            </div>
            <UserBalance :user="user" :start-date="startDate" :end-date="endDate" />
          </PanelWindow>
        </div>

        <!-- Second column: Event Registration -->
        <div class="flex flex-col gap-1.5 shrink-0 w-80 1.5xl:w-auto">
          <PanelWindow :title="authStore.isAdmin ? 'Registrer en fridag' : 'Kalender oversigt'" class="w-full"
            :showMaximize="true" @maximize="openModal">
            <!-- Admin-only event type picker -->
            <EventTypePicker v-if="authStore.isAdmin" :selected-event-type="eventTypePicked"
              @event-type-picked="handleEventTypePicked" @reset-calendar="resetCalendar" />
            <!-- Date range calendar -->
            <DateRangeCalendar :selected-range="selectedRange" :event-type="eventTypePicked" :user="user"
              @range-selected="handleRangeSelected" />
            <!-- Replacement Picker -->
            <ReplacementPicker v-if="authStore.isAdmin" :users="usersStore.users"
              @user-selected="handleReplacementSelected" />
            <!-- Admin-only buttons -->
            <div v-if="authStore.isAdmin" class="flex justify-end mt-6 space-x-2">
              <button @click="resetSelection"
                class="w-24 px-3.5 py-2.5 bg-neutral-100 text-neutral-800 text-center rounded-lg text-sm font-medium disabled:opacity-60 disabled:cursor-default enabled:hover:bg-neutral-200"
                :disabled="selectedRange.start === null &&
                  selectedRange.end === null &&
                  eventTypePicked === null
                  ">
                Nulstil
              </button>
              <div class="relative flex items-center justify-center w-24">
                <span class="absolute w-[58%] h-[66%] rounded bg-green-500 animate-ping"
                  :class="{ hidden: eventsLoading || buttonDisabled }"></span>
                <button @click="saveEvent"
                  class="relative w-24 px-3.5 py-2.5 text-white bg-green-600 disabled:bg-neutral-100 disabled:text-neutral-800 disabled:opacity-60 disabled:cursor-default enabled:hover:bg-green-700 font-medium rounded-lg text-sm text-center"
                  :disabled="eventsLoading || buttonDisabled">
                  Opret
                </button>
              </div>
            </div>
          </PanelWindow>
        </div>

        <!-- Third column: Future Events -->
        <div class="flex flex-col gap-1.5 w-80 shrink-0">
          <PanelWindow title="Fremtidige fridage" class="max-h-[calc(100vh-5rem)] rounded-lg">
            <UserEvents :user="user" />
          </PanelWindow>
        </div>
      </div>
    </div>
    <!-- Reusable modal for detailed view -->
    <ReusableModal :isOpen="showModal" :noButtons="true" @close="closeModal"
      :title="`Fraværskort: ${user.first_name} ${user.last_name}`">
      <DateRangeCalendar :yearly-view="true" :user="user" />
      <PagePrint :resolution="{ width: 1280, height: 1080 }" />
    </ReusableModal>
    <!-- Add the date range modal -->
    <ReusableModal 
      :isOpen="showDateRangeModal" 
      :isConfirm="true" 
      @close="closeDateRangeModal" 
      @submit="applyDateRange" 
      title="Vælg datointerval"
    >
      <DateRangeSelection 
        v-model:start-date="tempStartDate" 
        v-model:end-date="tempEndDate" 
      />
    </ReusableModal>
  </div>
</template>

<script setup>
import { computed, defineProps, inject, onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import DateRangeCalendar from "@/components/ui/UserDashboard/DateRangeCalendar.vue";
import EventTypePicker from "@/components/ui/UserDashboard/EventTypePicker.vue";
import ReplacementPicker from "@/components/ui/UserDashboard/ReplacementPicker.vue";
import UserBalance from "@/components/ui/UserDashboard/UserBalance.vue";
import UserInformation from "@/components/ui/UserDashboard/UserInformation.vue";
import UserEvents from "@/components/ui/UserDashboard/UserEvents.vue";
import PanelWindow from "@/components/ui/PanelWindow.vue";
import LoadingSpinner from "@/components/ui/LoadingSpinner.vue";
import confetti from "canvas-confetti";
import ReusableModal from "@/components/ui/ReusableModal.vue";
import PagePrint from "@/components/ui/PagePrint.vue";
import DateRangeSelection from "@/components/ui/AdminDashboard/DateRangeSelection.vue";
import { RotateCcw } from 'lucide-vue-next';

// Props and store injections
const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
});

const authStore = inject("authStore");
const eventsStore = inject("eventsStore");
const usersStore = inject("usersStore");

// Store refs
const { isLoading: eventsLoading } = storeToRefs(eventsStore);
const { isLoading: authLoading } = storeToRefs(authStore);
const { isLoading: usersLoading } = storeToRefs(usersStore);

// Component state
const selectedRange = ref({ start: null, end: null });
const eventTypePicked = ref(null);
const showModal = ref(false);
const selectedReplacement = ref(null);

// Add these date refs
const startDate = ref(new Date(new Date().getFullYear(), 0, 1));
const endDate = ref(new Date(new Date().getFullYear(), 11, 31));

// Add these refs for date range selection
const showDateRangeModal = ref(false);
const tempStartDate = ref(new Date(startDate.value));
const tempEndDate = ref(new Date(endDate.value));

// Computed properties
const buttonDisabled = computed(
  () =>
    !selectedRange.value.start ||
    !selectedRange.value.end ||
    !eventTypePicked.value
);

// Event handlers
const handleRangeSelected = (range) => {
  selectedRange.value = range;
};

const handleEventTypePicked = (eventType) => {
  eventTypePicked.value = eventType;
};

const handleReplacementSelected = (user) => {
  selectedReplacement.value = user;
};

// Reset functions
const resetSelection = () => {
  selectedRange.value = { start: null, end: null };
  eventTypePicked.value = null;
  selectedReplacement.value = null;
};

const resetCalendar = () => {
  selectedRange.value = { start: null, end: null };
};

// Confetti function
const triggerConfetti = () => {
  confetti({
    particleCount: 140,
    spread: 85,
    origin: { y: 0.6 },
  });
};

// Save event function
const saveEvent = async () => {
  if (buttonDisabled.value) {
    return;
  }

  const events = [];
  let currentDate = new Date(selectedRange.value.start);
  const end = selectedRange.value.end
    ? new Date(selectedRange.value.end)
    : null;

  const offset = currentDate.getTimezoneOffset();

  // Function to adjust date for timezone
  const adjustDate = (date) => {
    return new Date(date.getTime() - offset * 60 * 1000);
  };

  if (end) {
    // Date range selected
    while (currentDate <= end) {
      const adjustedDate = adjustDate(currentDate);
      events.push({
        holiday_type: eventTypePicked.value,
        event_date: adjustedDate.toISOString().split("T")[0],
        replacement_id: selectedReplacement.value ? selectedReplacement.value.id : null,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }
  } else {
    // Only start date selected
    const adjustedDate = adjustDate(currentDate);
    events.push({
      holiday_type: eventTypePicked.value,
      event_date: adjustedDate.toISOString().split("T")[0],
      replacement_id: selectedReplacement.value ? selectedReplacement.value.id : null,
    });
  }

  try {
    const success = await eventsStore.saveEvents(events, props.user.id);
    if (success) {
      triggerConfetti(); // Trigger confetti on successful save
      resetSelection();
      if (authStore.isAdmin) {
        await usersStore.fetchUsers();
      } else {
        await authStore.fetchLoggedInUser();
      }
    }
  } catch (error) {
    console.error("Error saving events:", error);
  }
};

// Modal functions
const openModal = () => {
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
};

// Add these functions for date range handling
function openDateRangeModal() {
  tempStartDate.value = new Date(startDate.value);
  tempEndDate.value = new Date(endDate.value);
  showDateRangeModal.value = true;
}

function closeDateRangeModal() {
  showDateRangeModal.value = false;
}

function applyDateRange() {
  startDate.value = new Date(tempStartDate.value);
  endDate.value = new Date(tempEndDate.value);
  closeDateRangeModal();
}

function formatDateRange(start, end) {
  const formatDate = (date) => {
    return new Intl.DateTimeFormat('da-DK', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      dateStyle: undefined
    }).format(date).replace(/\./g, '/');
  };
  return `${formatDate(start)} - ${formatDate(end)}`;
}

// Add this function in the script section
function resetToCurrentYear() {
  startDate.value = new Date(new Date().getFullYear(), 0, 1);
  endDate.value = new Date(new Date().getFullYear(), 11, 31);
}

// Lifecycle hook
onMounted(async () => {
  await eventsStore.fetchBalanceUpdateSchedules();
});
</script>